import React, {
  createContext, useContext, useLayoutEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Badge, Tooltip } from '@mantine/core';

/**
 * Valeurs propres au bloc (`seed`) du composant dont on règle un style dans
 * le panneau Thème ; vide partout ailleurs (inspecteur d'un bloc).
 */
export const StyleDefaultKeysContext = createContext([]);

// Les libellés viennent de Mantine (champs, interrupteurs) ou d'InspectorRow
// (types natifs de Puck, lignes composées) : on ne peut pas leur passer le
// badge en prop, on le pose dans le premier libellé rendu.
const LABEL_SELECTOR = '.mantine-InputWrapper-label, .mantine-Switch-label, [data-inspector-label]';

function DefaultBadge({ inline }) {
  return (
    <Tooltip
      label="Simple valeur de départ : un bloc la reçoit quand il prend ce style, puis peut la changer librement sans perdre le lien avec le style. La modifier ici ne touche pas les blocs déjà posés."
      multiline
      w={240}
      withArrow
      withinPortal
    >
      <Badge
        size="xs"
        variant="light"
        color="gray"
        ml={inline ? 6 : 0}
        // Dans un Switch, l'input invisible couvre tout le composant, libellé
        // compris : sans passer au-dessus, le badge ne reçoit jamais le survol.
        style={{
          cursor: 'help', verticalAlign: 'middle', position: 'relative', zIndex: 1,
        }}
      >
        par défaut
      </Badge>
    </Tooltip>
  );
}

/**
 * Marque un réglage du style comme simple valeur par défaut : le bloc la
 * reçoit en prenant le style, puis la garde pour lui (cf. `seed`,
 * shared/theme/styleSchema). Badge à droite du libellé, au-dessus du réglage
 * s'il n'en a pas. Rien hors du panneau Thème.
 */
export default function StyleDefaultMark({ propKey, children }) {
  const isDefault = useContext(StyleDefaultKeysContext).includes(propKey);
  const ref = useRef(null);
  // `undefined` tant que le libellé n'a pas été cherché : rien ne s'affiche
  // avant, pour ne pas voir le badge sauter d'une place à l'autre.
  const [label, setLabel] = useState(undefined);

  useLayoutEffect(() => {
    if (!isDefault) return;
    setLabel(ref.current?.querySelector(LABEL_SELECTOR) || null);
  });

  if (!isDefault) return children;
  return (
    <div ref={ref}>
      {label === null && <DefaultBadge />}
      {children}
      {label && createPortal(<DefaultBadge inline />, label)}
    </div>
  );
}
